/**
 * Whether the server last said a reader is enrolled in the study: `true`
 * after the personalised route served them, `false` after it answered
 * `not_enrolled`. Absent until either happens on this device.
 */
export const enrollmentKey = (readerId: string): string =>
  `enrollment:${readerId}`;

export interface EnrollmentMark {
  enrolled: boolean;
  heardAt: number;
}

export type KnownEnrollment = "enrolled" | "not_enrolled" | "unknown";

// A reader enrolled after the mark would otherwise commit the CDN's arms
// unflagged on every failed open.
export const NOT_ENROLLED_MARK_TTL_MS = 24 * 60 * 60 * 1000;

const isMark = (value: unknown): value is EnrollmentMark =>
  typeof value === "object" &&
  value !== null &&
  "enrolled" in value &&
  typeof value.enrolled === "boolean" &&
  "heardAt" in value &&
  typeof value.heardAt === "number" &&
  Number.isFinite(value.heardAt);

/**
 * What a stored mark still says at `now`. A not-enrolled mark older than its
 * TTL, or dated after `now` by a clock that moved back, says nothing; an
 * enrolled mark never expires.
 */
export function enrollmentAt(value: unknown, now: number): KnownEnrollment {
  if (!isMark(value)) return "unknown";
  if (value.enrolled) return "enrolled";
  const age = now - value.heardAt;
  return age >= 0 && age <= NOT_ENROLLED_MARK_TTL_MS
    ? "not_enrolled"
    : "unknown";
}
